import { useCallback, useEffect, useMemo, useState } from "react";
import { getPricebookItems } from "../../../../services";
import { PricebookItem } from "../../../../types/price-book.type";
import { ProductListItem } from "../../../../types/product.type";
import {
  PricebookLine,
  countLineChanges,
  findInvalidLineCodes,
  isSameNumberText,
  isValidDiscountPercent,
  toAmountText,
  toNewPricebookLine,
  toPricebookLine,
} from "./lineEdits";

const PAGE_SIZE = 100;

/**
 * PUT /pricebooks/:uuid/items REPLACES the whole product set, so the tab has to hold every
 * stored line before it can save — anything it never loaded would be dropped. The pages run
 * in sequence because the total only arrives with the first response.
 */
const fetchAllPricebookItems = async (
  uuid: string,
): Promise<PricebookItem[]> => {
  const rows: PricebookItem[] = [];
  let page = 1;
  let total = 0;
  do {
    const {
      data: { data },
    } = await getPricebookItems(uuid, page, PAGE_SIZE);
    total = data.count;
    rows.push(...data.rows);
    page += 1;
    if (data.rows.length === 0) break;
  } while (rows.length < total);
  return rows;
};

const toPricebookLines = (items: PricebookItem[]): PricebookLine[] =>
  items.flatMap((item) => {
    const line = toPricebookLine(item);
    return line ? [line] : [];
  });

const updateLine = (
  lines: PricebookLine[],
  productCode: string,
  changes: Partial<PricebookLine>,
): PricebookLine[] =>
  lines.map((line) =>
    line.productCode === productCode ? { ...line, ...changes } : line,
  );

/**
 * The pricebook's lines and book % as stored, and the edits made to them on this page.
 * Nothing is written until the caller saves; `changeCount` is what the unsaved bar reports.
 */
export function usePricebookEditor(
  pricebookUuid: string,
  storedBookDiscount: number,
) {
  const [storedLines, setStoredLines] = useState<PricebookLine[]>([]);
  const [lines, setLines] = useState<PricebookLine[]>([]);
  // Null while the book % is untouched, so a re-read header is shown as it is.
  const [bookDiscountDraft, setBookDiscountDraft] = useState<string | null>(
    null,
  );
  const [invalidCodes, setInvalidCodes] = useState<string[]>([]);
  // Stored lines with no product code: they cannot be sent back, so the next save drops them.
  const [codelessLineCount, setCodelessLineCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadFailed, setLoadFailed] = useState<boolean>(false);
  const [reloadCount, setReloadCount] = useState<number>(0);

  useEffect(() => {
    // Reopening another book, or a reload after a save, makes an in-flight read stale.
    let ignore = false;
    setLoading(true);
    setLoadFailed(false);

    const load = async () => {
      try {
        const items = await fetchAllPricebookItems(pricebookUuid);
        if (ignore) return;
        const loaded = toPricebookLines(items);
        setStoredLines(loaded);
        setCodelessLineCount(items.length - loaded.length);
        setLines(loaded);
        setBookDiscountDraft(null);
        setInvalidCodes([]);
      } catch {
        // The axios interceptor shows the failure; the save stays shut without the lines.
        if (!ignore) setLoadFailed(true);
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    load();
    return () => {
      ignore = true;
    };
  }, [pricebookUuid, reloadCount]);

  const storedBookDiscountText = toAmountText(storedBookDiscount);
  const bookDiscountText = bookDiscountDraft ?? storedBookDiscountText;
  const isBookDiscountChanged = !isSameNumberText(
    bookDiscountText,
    storedBookDiscountText,
  );
  const isBookDiscountValid = isValidDiscountPercent(bookDiscountText);

  const lineChangeCount = useMemo(
    () => countLineChanges(storedLines, lines),
    [storedLines, lines],
  );
  const changeCount = lineChangeCount + (isBookDiscountChanged ? 1 : 0);

  const clearInvalid = (productCode: string) =>
    setInvalidCodes((prev) => prev.filter((code) => code !== productCode));

  const changeBookDiscount = (text: string) => setBookDiscountDraft(text);

  const changeUnitPrice = (productCode: string, unitPrice: string) => {
    setLines((prev) => updateLine(prev, productCode, { unitPrice }));
    clearInvalid(productCode);
  };

  const changeOwnDiscount = (productCode: string, ownDiscountPercent: string) => {
    setLines((prev) => updateLine(prev, productCode, { ownDiscountPercent }));
    clearInvalid(productCode);
  };

  const toggleStatus = (productCode: string, isEnabled: boolean) => {
    setLines((prev) =>
      updateLine(prev, productCode, {
        status: isEnabled ? "enable" : "disable",
      }),
    );
  };

  const removeLine = (productCode: string) => {
    setLines((prev) => prev.filter((line) => line.productCode !== productCode));
    clearInvalid(productCode);
  };

  // A line taken off and put back in the same session comes back exactly as stored, so its
  // unit and FOC / discount settings survive; a new product starts at its ราคาหน้าซอง.
  const addProducts = (products: ProductListItem[]) => {
    const storedByCode = new Map(
      storedLines.map((line) => [line.productCode, line]),
    );
    setLines((prev) => {
      const currentCodes = new Set(prev.map((line) => line.productCode));
      const added = products
        .filter((product) => !currentCodes.has(product.code))
        .map(
          (product) =>
            storedByCode.get(product.code) ?? toNewPricebookLine(product),
        );
      return [...prev, ...added];
    });
  };

  const discard = () => {
    setLines(storedLines);
    setBookDiscountDraft(null);
    setInvalidCodes([]);
  };

  /** Flags every line the API would refuse, and answers with their codes. */
  const flagInvalidLines = (): string[] => {
    const invalid = findInvalidLineCodes(lines);
    setInvalidCodes(invalid);
    return invalid;
  };

  const reload = useCallback(() => setReloadCount((count) => count + 1), []);

  return {
    storedLines,
    lines,
    loading,
    loadFailed,
    codelessLineCount,
    bookDiscountText,
    isBookDiscountChanged,
    isBookDiscountValid,
    changeCount,
    invalidCodes,
    changeBookDiscount,
    changeUnitPrice,
    changeOwnDiscount,
    toggleStatus,
    removeLine,
    addProducts,
    discard,
    flagInvalidLines,
    reload,
  };
}

export type PricebookEditor = ReturnType<typeof usePricebookEditor>;
